/**
 * Orders Store
 * Manages order state and real-time updates
 */

import { create } from 'zustand';
import { Order } from '@/lib/constants';
import { ordersApi } from '@/lib/api';
import { logger } from '@/lib/logger';

interface OrdersState {
  orders: Order[];
  activeOrders: Order[];
  pastOrders: Order[];
  currentOrder: Order | null;
  
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchOrders: () => Promise<void>;
  createOrder: (orderData: any) => Promise<Order>;
  fetchOrder: (orderId: string) => Promise<void>;
  cancelOrder: (orderId: string, reason: string) => Promise<void>;
  updateOrderStatus: (orderId: string, status: string) => void; // For WebSocket updates
  clearError: () => void;
}

export const useOrdersStore = create<OrdersState>((set, get) => ({
  orders: [],
  activeOrders: [],
  pastOrders: [],
  currentOrder: null,
  isLoading: false,
  error: null,

  fetchOrders: async () => {
    set({ isLoading: true, error: null });
    try {
      const orders = await ordersApi.getOrders();
      
      // Categorize orders
      const activeStatuses = ['pending', 'placed', 'accepted', 'preparing', 'ready_for_pickup', 'rider_enroute', 'picked_up', 'out_for_delivery', 'in_transit'];
      const activeOrders = orders.filter((order: Order) => activeStatuses.includes(order.status));
      const pastOrders = orders.filter((order: Order) => ['delivered', 'cancelled', 'refunded'].includes(order.status));
      
      set({
        orders,
        activeOrders,
        pastOrders,
        isLoading: false,
      });
      
      logger.info('Orders loaded', { 
        total: orders.length,
        active: activeOrders.length,
        past: pastOrders.length,
      });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load orders';
      set({ isLoading: false, error: errorMessage });
      logger.error('Failed to fetch orders', error as Error);
    }
  },

  createOrder: async (orderData: any) => {
    set({ isLoading: true, error: null });
    try {
      const order = await ordersApi.createOrder(orderData);
      
      // Add to orders list
      const { orders, activeOrders } = get();
      set({
        orders: [order, ...orders],
        activeOrders: [order, ...activeOrders],
        currentOrder: order,
        isLoading: false,
      });
      
      logger.info('Order created', { orderId: order.id });
      return order;
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to create order';
      set({ isLoading: false, error: errorMessage });
      logger.error('Failed to create order', error as Error);
      throw error;
    }
  },

  fetchOrder: async (orderId: string) => {
    set({ isLoading: true, error: null });
    try {
      const order = await ordersApi.getOrder(orderId);
      set({ currentOrder: order, isLoading: false });
      logger.info('Order loaded', { orderId: order.id });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load order';
      set({ isLoading: false, error: errorMessage });
      logger.error('Failed to fetch order', error as Error);
    }
  },

  cancelOrder: async (orderId: string, reason: string) => {
    set({ isLoading: true, error: null });
    try {
      await ordersApi.cancelOrder(orderId, reason);
      
      // Update order status in state
      const { orders, activeOrders, pastOrders } = get();
      
      const updatedOrders = orders.map((order) =>
        order.id === orderId ? { ...order, status: 'cancelled' as const } : order
      );
      
      const updatedActiveOrders = activeOrders.filter((order) => order.id !== orderId);
      const cancelledOrder = orders.find((order) => order.id === orderId);
      const updatedPastOrders = cancelledOrder 
        ? [{ ...cancelledOrder, status: 'cancelled' as const }, ...pastOrders]
        : pastOrders;
      
      set({
        orders: updatedOrders,
        activeOrders: updatedActiveOrders,
        pastOrders: updatedPastOrders,
        isLoading: false,
      });
      
      logger.info('Order cancelled', { orderId, reason });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to cancel order';
      set({ isLoading: false, error: errorMessage });
      logger.error('Failed to cancel order', error as Error);
      throw error;
    }
  },

  updateOrderStatus: (orderId: string, status: string) => {
    const { orders, currentOrder } = get();
    
    // Update in all lists
    const updatedOrders = orders.map((order) =>
      order.id === orderId ? { ...order, status: status as any } : order
    );
    
    // Recategorize
    const activeStatuses = ['placed', 'accepted', 'preparing', 'ready_for_pickup', 'rider_enroute', 'picked_up', 'out_for_delivery', 'in_transit'];
    const updatedActiveOrders = updatedOrders.filter((order) => activeStatuses.includes(order.status));
    const updatedPastOrders = updatedOrders.filter((order) => ['delivered', 'cancelled', 'refunded'].includes(order.status));
    
    // Update current order if it's the one that changed
    const updatedCurrentOrder = currentOrder?.id === orderId 
      ? { ...currentOrder, status: status as any }
      : currentOrder;
    
    set({
      orders: updatedOrders,
      activeOrders: updatedActiveOrders,
      pastOrders: updatedPastOrders,
      currentOrder: updatedCurrentOrder,
    });
    
    logger.info('Order status updated via WebSocket', { orderId, status });
  },

  clearError: () => set({ error: null }),
}));









